import { Grid, InputAdornment } from '@mui/material';
import { FormElements, NotifyIcon } from 'web-components';
import { useTranslation } from 'react-i18next';
import { CustomTreeView } from '../../../../../../components/CustomTreeView';

type SelectedProductLineInterface = {
  title: string;
  value: string;
};

type SelectedMachineInterface = {
  id: string;
  title: string;
  value: string;
};

type MultiSelectOption = {
  title: string;
  value: string;
};

type MachineOptionInterface = {
  id: string;
  title: string;
  value: string;
};

type SensorOptionInterface = {
  title: string;
  value: string;
  type: string;
};

type TreeSensorInterface = {
  is_custom: boolean;
  type: string;
};

type FilteredTreeDataInterface = {
  machines: {
    id: string;
    name: string;
    sensors: TreeSensorInterface[];
  }[];
  name: string;
}[];

type SelectedItemsInterface = {
  machine_id: string;
  type: string;
  auto_scale: boolean;
  line_color: string;
  line_style: string;
  line_thickness: number;
  max_scale: number;
  min_scale: number;
  is_custom: boolean;
}[];

interface Step1Props {
  selectedProductLines: SelectedProductLineInterface[];
  handleProductLineChange: () => void;
  productionLineOptions: MultiSelectOption[];
  selectedMachines: SelectedMachineInterface[];
  handleMachineChange: () => void;
  machineOptions: MachineOptionInterface[];
  selectedSensors: SensorOptionInterface[];
  handleSensorChange: () => void;
  sensorOptions: SensorOptionInterface[];
  filteredTreeData: FilteredTreeDataInterface;
  selectedItems: SelectedItemsInterface;
  handleSelectionChange: () => void;
}

const Step1 = ({
  selectedProductLines,
  handleProductLineChange,
  productionLineOptions,
  selectedMachines,
  handleMachineChange,
  machineOptions,
  selectedSensors,
  handleSensorChange,
  sensorOptions,
  filteredTreeData,
  selectedItems,
  handleSelectionChange
}: Step1Props) => {
  const { t } = useTranslation();
  return (
    <Grid container spacing={2}>
      <Grid item xs={12} md={6}>
        <FormElements.MultiSelect
          activeColor="black"
          optionType="checkbox"
          label={t('dialog.add_new_template.filter_by_product_line')}
          name="product_line"
          id="product_line"
          bgcolor="#EFF1F3"
          fullWidth
          value={selectedProductLines}
          setFieldValue={handleProductLineChange}
          options={Array.isArray(productionLineOptions) ? productionLineOptions : []}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end" style={{ position: 'absolute', right: '12px', bottom: '22px' }}>
                <NotifyIcon iconName="filterOutlined" fontSize="default" color="primary" />
              </InputAdornment>
            )
          }}
          data-testid="production-line-template-checkbox-filter"
          sx={{
            mt: { md: 1 },
            mb: { md: 1 }
          }}
        />
      </Grid>
      <Grid item xs={12} md={6}>
        <FormElements.MultiSelect
          activeColor="black"
          optionType="checkbox"
          label={t('dialog.add_new_template.filter_by_machine')}
          name="filter_machine"
          id="filter_machine"
          bgcolor="#EFF1F3"
          fullWidth
          multiple
          value={selectedMachines}
          setFieldValue={handleMachineChange}
          options={Array.isArray(machineOptions) ? machineOptions : []}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end" style={{ position: 'absolute', right: '12px', bottom: '22px' }}>
                <NotifyIcon iconName="filterOutlined" fontSize="default" color="primary" />
              </InputAdornment>
            )
          }}
          data-testid="by-machine-template-checkbox-filter"
          sx={{
            mt: { md: 1 },
            mb: { md: 1 }
          }}
        />
      </Grid>
      <Grid item xs={12}>
        <FormElements.MultiSelect
          activeColor="black"
          optionType="checkbox"
          label={t('dialog.add_new_template.filter_by_sensor')}
          name="filter_sensor"
          id="filter_sensor"
          bgcolor="#EFF1F3"
          fullWidth
          multiple
          value={selectedSensors}
          setFieldValue={handleSensorChange}
          options={Array.isArray(sensorOptions) ? sensorOptions : []}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end" style={{ position: 'absolute', right: '12px', bottom: '22px' }}>
                <NotifyIcon iconName="filterOutlined" fontSize="default" color="primary" />
              </InputAdornment>
            )
          }}
          data-testid="sensor-template-checkbox-filter"
          sx={{
            mt: { md: 1 },
            mb: { md: 1 }
          }}
        />
      </Grid>
      <Grid item xs={12}>
        <CustomTreeView
          rawData={filteredTreeData}
          selectedItems={selectedItems}
          onSelectionChange={handleSelectionChange}
        />
      </Grid>
    </Grid>
  );
};

export { Step1 };
